import { DeleteOutlined, ExclamationCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Input, Modal, Tag, Typography } from "antd";
import queryClient from "api/queryClient.ts";
import { useGetMyTenantsQuery, useLeaveTenantMutation } from "api/Tenant/hooks";
import { CreateTenantModal } from "components/Modals/CreateTenantModal/CreateTenantModal";
import { ResponseWithPagination, Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useState, type MouseEvent } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { UserRequestStatus } from "types/enums";
import { TableId } from "types/models";
import { getTagDefaultColor } from "utils/getStatusDefaultBackground.ts";
import { getTenantRoleBadgeColor, getTenantRoleGroups } from "utils/getTenantRoleBadge.ts";

import MyTenantData = App.Data.MyTenantData;
import GetMyTenantsResponse = App.Http.Responses.Tenant.GetMyTenantsResponse;

export const ActiveProgramsTable = () => {
    const tableId = TableId.ActiveProgramsTable;
    const { t } = usePaldockTranslation("tables.active-programs");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tGeneral } = usePaldockTranslation("general");

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [leaveTenantTarget, setLeaveTenantTarget] = useState<MyTenantData | null>(null);
    const [leaveSlugInput, setLeaveSlugInput] = useState("");

    const tableAtomInstance = tableAtom<MyTenantData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));

    const leaveTenantMutation = useLeaveTenantMutation();
    const navigate = useNavigate();

    const getRowHandlers = (record: MyTenantData) => ({
        onClick: (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest("button") || target.closest("a")) return;
            void navigate({ to: "/$tenantSlug", params: { tenantSlug: record.slug_name } });
        },
        onMouseDown: (e: MouseEvent) => {
            if (e.button === 1) {
                e.preventDefault();
                window.open(`/${record.slug_name}`, "_blank", "noopener,noreferrer");
            }
        },
        style: { cursor: "pointer" },
    });

    const statusFilterOptions = [
        { label: tGeneral("request_status.accepted"), value: UserRequestStatus.Accepted },
        { label: tGeneral("request_status.pending"), value: UserRequestStatus.Pending },
        { label: tGeneral("request_status.rejected"), value: UserRequestStatus.Rejected },
        { label: tGeneral("request_status.left"), value: UserRequestStatus.Left },
        { label: tGeneral("request_status.blocked"), value: UserRequestStatus.Blocked },
        { label: tGeneral("request_status.kicked"), value: UserRequestStatus.Kicked },
    ];

    const handleLeaveTenant = (tenant: MyTenantData) => {
        setLeaveTenantTarget(tenant);
        setLeaveSlugInput("");
    };

    const handleCloseLeaveModal = () => {
        setLeaveTenantTarget(null);
        setLeaveSlugInput("");
    };

    const handleConfirmLeave = async () => {
        if (!leaveTenantTarget) return;
        try {
            await leaveTenantMutation.mutateAsync(leaveTenantTarget.id);
            toast.success(t("actions.leave_success"));
            queryClient.invalidateQueries({ queryKey: ["me"] });
            tableFetchDataUtils.refetchData();
            handleCloseLeaveModal();
        } catch {
            toast.error(t("actions.leave_error"));
        }
    };

    const columnConfig: TableColumnType<MyTenantData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.logo")} />,
            dataIndex: "logo",
            key: "logo",
            render: (_, record) =>
                record.logo ? (
                    <img src={record.logo} alt="Logo" style={{ height: "32px" }} className="object-contain" />
                ) : (
                    <EmptyCell />
                ),
            width: 80,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.name")} />,
            dataIndex: "title",
            key: "title",
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "ascend",
            ellipsis: true,
            width: 250,
            filterable: true,
            filterType: FILTER_TYPES.string,
            render: (_, record) => (
                <Link
                    to="/$tenantSlug"
                    params={{ tenantSlug: record.slug_name }}
                    className="underline text-blue-600"
                    onClick={(e) => e.stopPropagation()}
                >
                    {record.title}
                </Link>
            ),
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.role")} />,
            dataIndex: "roles",
            key: "roles",
            render: (_, record) => {
                const roleGroups = getTenantRoleGroups(record.roles?.map((role) => role.name));
                if (!roleGroups.length) return <EmptyCell />;

                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        {roleGroups.map((group) => (
                            <Badge
                                key={group}
                                count={tC(`roles.${group}`)}
                                color={getTenantRoleBadgeColor(group)}
                                classNames={{ indicator: "!rounded !shadow-none" }}
                            />
                        ))}
                    </div>
                );
            },
            ellipsis: true,
            sorter: true,
            width: 150,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "roles.name",
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.balance")} />,
            dataIndex: "balance",
            key: "balance",
            render: (_, record) => record.balance?.formatted_amount ?? "-",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.status")} />,
            dataIndex: "request_status",
            key: "request_status",
            render: (status: UserRequestStatus | null) => {
                if (!status) return <Tag color="green">{tGeneral("request_status.accepted")}</Tag>;
                return <Tag color={getTagDefaultColor(status)}>{tGeneral(`request_status.${status}`)}</Tag>;
            },
            ellipsis: true,
            sorter: true,
            width: 120,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: statusFilterOptions,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.actions")} />,
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleLeaveTenant(record);
                    }}
                />
            ),
            width: 80,
        },
    ];

    const fetchMyTenantsQuery = useGetMyTenantsQuery(
        {
            status: "active",
            include_roles: 1,
            include_request_status: 1,
            include_balance: 0,
            page: {
                number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
                size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
            },
            filter: {
                filter: tableState.filter?.filter ?? {},
                sort: tableState.filter?.sort,
            },
        },
        { enabled: false }
    );

    const tableFetchDataUtils = useTableDataApiOperations<GetMyTenantsResponse, void, MyTenantData>(
        tableId,
        fetchMyTenantsQuery
    );

    return (
        <>
            <Table<ResponseWithPagination, MyTenantData>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetMyTenantsResponse}
                dataSource={tableFetchDataUtils.tableData?.tenants || []}
                rowKey={(record) => record.id}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableFilters={true}
                loading={tableFetchDataUtils.fetchQuery.isFetching || leaveTenantMutation.isPending}
                autoScrollWidth={true}
                onRow={(record) => getRowHandlers(record)}
                rowClassName={() => "hover:bg-gray-50"}
                extraTopBarElements={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
                        {t("actions.create_program")}
                    </Button>
                }
            />
            <CreateTenantModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
            <Modal
                destroyOnClose
                title={
                    <span className="flex items-center gap-2 text-red">
                        <ExclamationCircleOutlined />
                        {t("actions.leave_slug_title")}
                    </span>
                }
                open={!!leaveTenantTarget}
                onCancel={handleCloseLeaveModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseLeaveModal}>
                        {tC("cancel")}
                    </Button>,
                    <Button
                        key="leave"
                        danger
                        type="primary"
                        disabled={leaveSlugInput !== leaveTenantTarget?.slug_name}
                        loading={leaveTenantMutation.isPending}
                        onClick={handleConfirmLeave}
                    >
                        {t("actions.leave")}
                    </Button>,
                ]}
            >
                <Typography.Title level={4} className="!mb-3">
                    {leaveTenantTarget?.title}
                </Typography.Title>
                <p>{t("actions.leave_confirm_message")}</p>
                <div className="mt-4">
                    <Typography.Text strong>{tC("workspace_identifier")}</Typography.Text>
                    <div className="mt-1">
                        <Typography.Text copyable code>
                            {leaveTenantTarget?.slug_name}
                        </Typography.Text>
                    </div>
                </div>
                <div className="mt-3">
                    <Typography.Text strong>{tC("paste_to_confirm")}</Typography.Text>
                    <Input
                        className="mt-2"
                        placeholder={leaveTenantTarget?.slug_name}
                        value={leaveSlugInput}
                        onChange={(e) => setLeaveSlugInput(e.target.value)}
                    />
                </div>
            </Modal>
        </>
    );
};
